import BaseStepHandler from './BaseStepHandler';
import { filters } from 'fabric';

/**
 * Handler for color step type
 */
export default class ColorStepHandler extends BaseStepHandler {
    /**
     * Initialize color elements
     */
    initialize() {
        super.initialize();
        
        // Get the main element ID from definition
        const elementId = this.definition.elements?.[0];
        if (!elementId) return this;
        
        // Get element from canvas
        this.colorElement = this.canvas.getElement(elementId);
        this.elementData = this.canvas.getElementData(elementId);

        // Initialize debounce timer
        this.colorDebounceTimer = null;
        this.renderDebounceTimer = null;
        
        // Create debounced functions
        this.debouncedApplyColor = this.debounce((color) => {
            this.applyOverlayColorToElement(color);
        }, 150); // 150ms delay for good responsiveness

        this.debouncedNotifyChanges = this.debounce((stepData) => {
            this.setStepData(stepData, true); // Notify changes with debounce
        }, 300); // 300ms delay for change notifications

        // Element should already exist from template
        if (this.colorElement) {
            // Store reference
            this.elements.set(elementId, this.colorElement);

            // Store original color if available (check both overlayColor and fill)
            if (this.elementData?.data?.overlayEnabled) {
                this.originalColor = this.elementData.data.overlayColor || this.elementData.data.fill;
                this.applyOverlayColorToElement(this.originalColor);
            } else if (this.elementData?.data?.fill) {
                // Also store fill color if no overlay is enabled
                this.originalColor = this.elementData.data.fill;
            }
        }
        
        return this;
    }

    /**
     * Debounce utility function
     * @param {Function} func - Function to debounce
     * @param {number} delay - Delay in milliseconds
     * @returns {Function} Debounced function
     */
    debounce(func, delay) {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    }

    /**
     * Clear all debounce timers
     */
    clearDebounceTimers() {
        if (this.colorDebounceTimer) {
            clearTimeout(this.colorDebounceTimer);
            this.colorDebounceTimer = null;
        }
        if (this.renderDebounceTimer) {
            clearTimeout(this.renderDebounceTimer);
            this.renderDebounceTimer = null;
        }
    }
    
    /**
     * Process template when no data exists
     */
    processTemplate() {
        // Only log for border2
        if (this.stepId === 'border2') {
            console.log('🎨 [BORDER2_PROCESS_TEMPLATE] Starting for step:', this.stepId);
        }

        // For color steps, we just need to make sure the element is visible
        // since it's typically part of the template and should be visible
        this.show();

        // Check if we have existing data to apply from edit mode
        const stepData = this.getStepData();
        if (this.stepId === 'border2') {
            console.log('🎨 [BORDER2_PROCESS_TEMPLATE] StepData:', stepData);
        }

        // Check if template has element data with fill color (from edit mode conversion)
        const elementId = this.definition.elements?.[0];
        if (this.stepId === 'border2') {
            console.log('🎨 [BORDER2_PROCESS_TEMPLATE] Element ID:', elementId);
        }

        if (elementId) {
            const elementData = this.canvas.getElementData(elementId);
            if (this.stepId === 'border2') {
                console.log('🎨 [BORDER2_PROCESS_TEMPLATE] Element data:', elementData);
                console.log('🎨 [BORDER2_PROCESS_TEMPLATE] Fill color:', elementData?.data?.fill);
                console.log('🎨 [BORDER2_PROCESS_TEMPLATE] Overlay color:', elementData?.data?.overlayColor);
                console.log('🎨 [BORDER2_PROCESS_TEMPLATE] Overlay enabled:', elementData?.data?.overlayEnabled);
            }

            // If element has fill color from template, use it
            if (elementData?.data?.fill && !elementData.data.fill.includes('rgb(0,0,0)')) {
                let color = elementData.data.fill;

                if (this.stepId === 'border2') {
                    console.log('✅ [BORDER2_PROCESS_TEMPLATE] Using fill color:', color);
                }

                // Convert black to white for edit mode (prevents darkening elements)
              /*  if (color.toLowerCase() === '#000000' || color.toLowerCase() === '#000') {
                    color = '#ffffff';
                }*/

                // Initialize stepData with color from element
                const initialStepData = {
                    color: color,
                    enabled: true
                };

                this.setStepData(initialStepData);

                // Verify what was saved
                const savedStepData = this.getStepData();
                // console.log('💾 [COLOR_PROCESS_TEMPLATE] Saved stepData:', savedStepData);

                this.applyOverlayColorToElement(color);

                // Only mark as touched if we're loading from edit mode
                // (not on first load of template)
                if (this.canvas.designer?.template?.editMode) {
                    this.inputTouched = true;
                    // console.log('✓ [COLOR_PROCESS_TEMPLATE] Marked as touched (edit mode)');
                } else {
                    // console.log('○ [COLOR_PROCESS_TEMPLATE] NOT marked as touched (default template)');
                }

                return this;
            }
        }

        // Fallback: check if we have existing stepData
        if (stepData && (stepData.color || stepData.colorContent?.color)) {
            const color = stepData.colorContent?.color || stepData.color;
            // console.log('🔄 [COLOR_PROCESS_TEMPLATE] Using fallback color:', color);
            this.applyOverlayColorToElement(color);
        }

        // console.log('⬅️ [COLOR_PROCESS_TEMPLATE] Finished');
        return this;
    }
    
    /**
     * Update color element with step data
     * 
     * @param {object} stepData - Step data
     */
    update(stepData) {
        super.update(stepData);

        // Apply color immediately without debounce when updating from external source
        this.applyOverlayColorToElement(stepData.color);
        
        return this;
    }

    /**
     * Apply overlay color to element with optimized rendering
     * @param {string} color - Color value to apply
     */
    applyOverlayColorToElement(color) {
        if (!this.colorElement) return;

        // Clear existing filters
        this.colorElement.filters = [];

        if (color) {
            // Apply new color filter
            this.colorElement.filters.push(new filters.BlendColor({
                color: color,
                mode: 'multiply',
                alpha: 1
            }));
        }
        
        // Apply filters and request render
        this.colorElement.applyFilters();
        this.canvas.canvas.requestRenderAll();
    }

    /**
     * Apply color immediately for instant visual feedback (lightweight)
     * @param {string} color - Color value to apply
     */
    applyColorImmediate(color) {
        if (!this.colorElement) return;

        // For very fast feedback, we could potentially use a lighter approach
        // but for now, we'll rely on the debounced version for performance
        // This method is here for future optimization if needed
    }
    
    /**
     * Render panel content
     */
    renderPanelContent() {
        console.log('🎨 [COLOR_RENDER_PANEL] Step:', this.stepId);

        const stepData = this.getStepData();
        console.log('🎨 [COLOR_RENDER_PANEL] StepData:', stepData);
        console.log('🎨 [COLOR_RENDER_PANEL] stepData.color:', stepData?.color);
        console.log('🎨 [COLOR_RENDER_PANEL] stepData.colorContent:', stepData?.colorContent);

        // Get color from stepData (handles both new and edit mode)
        // Priority: stepData.color > elementData.fill > elementData.overlayColor > default
        let color = stepData?.color || stepData?.colorContent?.color;

        if (!color) {
            // Check if element has fill color from edit mode
            const elementId = this.definition.elements?.[0];
            if (elementId) {
                const elementData = this.canvas.getElementData(elementId);
                console.log('🎨 [COLOR_RENDER_PANEL] elementData.fill:', elementData?.data?.fill);
                console.log('🎨 [COLOR_RENDER_PANEL] elementData.overlayColor:', elementData?.data?.overlayColor);
                color = elementData?.data?.fill || elementData?.data?.overlayColor;
            }
        }

        color = color || '#000000';

        console.log('🎨 [COLOR_RENDER_PANEL] Final color for input:', color);

        // Convert black to white for edit mode (prevents darkening elements)
       /* if (color.toLowerCase() === '#000000' || color.toLowerCase() === '#000') {
            color = '#ffffff';
        }*/

        return `
            <div class="stc-color-field">
                <input type="color"
                       class="stc-color-picker"
                       value="${color}"
                       ${this.definition.required ? 'required' : ''}>
            </div>
        `;
    }
    
    /**
     * Bind panel events
     */
    bindPanelEvents() {
        if (!this.panelElement) return;
        
        // Get UI elements
        const colorPicker = this.panelElement.querySelector('.stc-color-picker');
        const clearButton = this.panelElement.querySelector('.stc-clear-step');
        
        // Bind color picker events with debounce
        if (colorPicker) {
            // Handle input event (fires frequently during drag)
            this.addEventListener(colorPicker, 'input', (e) => {
                const color = e.target.value;
                const stepData = this.getStepData() || {};
                stepData.color = color;
                this.inputTouched = true;
                
                // Update step data immediately but don't notify changes yet
                this.setStepData(stepData, false);
                
                // Apply color with debounce to prevent excessive re-renders
                this.debouncedApplyColor(color);

                // Notify changes with longer debounce
                this.debouncedNotifyChanges(stepData);
                
                if (clearButton) clearButton.disabled = false;
            });

            // Handle change event (fires when user is done selecting)
            this.addEventListener(colorPicker, 'change', (e) => {
                const color = e.target.value;
                const stepData = this.getStepData() || {};
                stepData.color = color;
                this.inputTouched = true;
                
                // Clear any pending debounced calls
                this.clearDebounceTimers();

                // Apply immediately on change (user is done selecting)
                this.applyOverlayColorToElement(color);
                this.setStepData(stepData, true);
                
                if (clearButton) clearButton.disabled = false;
            });
        }
        
        // Bind clear button event
        if (clearButton) {
            this.addEventListener(clearButton, 'click', () => {
                this.clear();
            });
        }
    }

    /**
     * Step-specific reset logic
     */
    onReset() {
        // Clear any pending debounced operations
        this.clearDebounceTimers();
        
        // Get main element
        const elementId = this.definition.elements?.[0];
        if (!elementId) return;
        
        const element = this.getElement(elementId);
        if (!element) return;
        
        // Remove all filters
        element.filters = [];
        element.applyFilters();
        
        // If we stored the original color, restore it
        if (this.originalColor) {
            element.set('fill', this.originalColor);
        }
        
        // Request final render
        this.canvas.canvas.requestRenderAll();
    }

    /**
     * Get export data for color step
     *
     * @returns {object} Export data
     */
    getStepExportData() {
        // Get base export data
        const exportData = super.getStepExportData();
        const stepData = this.getStepData();

        // Get main element ID
        const elementId = this.definition.elements?.[0];
        const element = elementId ? this.elements.get(elementId) : null;

        // Determine color value intelligently:
        // - In EDIT mode: ALWAYS use current color from input picker (even if not touched)
        // - In NEW mode: Use stepData if touched, otherwise preserve original
        let colorValue;

        // Check if we're in edit mode
        const isEditMode = this.canvas.designer?.template?.editMode;

        if (isEditMode) {
            // EDIT MODE: Always read current color from the actual element on canvas
            // This is the VISUAL color the user sees, regardless of whether they opened the panel

            // First try to get from the element's current overlay color (applied to canvas)
            const elementData = elementId ? this.canvas.getElementData(elementId) : null;

            if (elementData?.data?.overlayColor) {
                // Element has overlay color set
                colorValue = elementData.data.overlayColor;
                if (this.stepId === 'border2') {
                    console.log('💾 [BORDER2_EXPORT] EDIT MODE - Using element overlay color:', colorValue);
                }
            } else if (elementData?.data?.fill) {
                // Element has fill color
                colorValue = elementData.data.fill;
                if (this.stepId === 'border2') {
                    console.log('💾 [BORDER2_EXPORT] EDIT MODE - Using element fill color:', colorValue);
                }
            } else if (element?.fill) {
                // Try getting from the fabric element directly
                colorValue = typeof element.fill === 'string' ? element.fill : '#ffffff';
                if (this.stepId === 'border2') {
                    console.log('💾 [BORDER2_EXPORT] EDIT MODE - Using fabric element fill:', colorValue);
                }
            } else if (stepData.color !== undefined) {
                // Fallback to stepData
                colorValue = stepData.color;
                if (this.stepId === 'border2') {
                    console.log('💾 [BORDER2_EXPORT] EDIT MODE - Using stepData color:', colorValue);
                }
            } else {
                // Last resort fallback
                colorValue = this.originalColor || '#ffffff';
                if (this.stepId === 'border2') {
                    console.log('💾 [BORDER2_EXPORT] EDIT MODE - Using fallback color:', colorValue);
                }
            }
        } else {
            // NEW MODE: Use original logic (preserve if not touched)
            if (this.inputTouched && stepData.color !== undefined) {
                // User interacted with the color picker, use their choice
                colorValue = stepData.color;
                // console.log('💾 [COLOR_EXPORT] NEW MODE - Using user-chosen color:', colorValue);
            } else {
                // User did NOT touch the color picker, preserve original
                const elementData = elementId ? this.canvas.getElementData(elementId) : null;

                colorValue = this.originalColor
                             || elementData?.data?.fill
                             || elementData?.data?.overlayColor
                             || '#ffffff';  // White as last resort (instead of black)

                // console.log('💾 [COLOR_EXPORT] NEW MODE - Preserving original color:', colorValue);
            }
        }

        // Add color-specific data
        exportData.colorContent = {
            targetElement: elementId,
            color: colorValue,

            // Get the original element color if available
            originalColor: this.originalColor || (element && element.fill ? element.fill : null),

            // Get blend mode information if using a filter
            blendInfo: element && element.filters && element.filters.length > 0
                ? {
                    mode: 'multiply', // Default mode used in update()
                    alpha: 1          // Default alpha used in update()
                }
                : null
        };

        return exportData;
    }

    /**
     * Check if step is complete
     * @returns {boolean} Whether the step has been touched/modified
     */
    isComplete() {
        return this.inputTouched;
    }

    /**
     * Clean up handler resources
     * @returns {BaseStepHandler} this instance for chaining
     */
    dispose() {
        // Clear any pending debounced operations
        this.clearDebounceTimers();
        
        // Call parent dispose
        super.dispose();
        
        return this;
    }

    /**
     * Deactivate this step
     * Called when another step becomes active
     * @returns {BaseStepHandler} this instance for chaining
     */
    deactivate() {
        // Clear any pending operations when deactivating
        this.clearDebounceTimers();
        
        // Call parent deactivate
        super.deactivate();
        
        return this;
    }
}